// Navbar.jsx
// Driven entirely by siteConfig.nav and siteConfig.brand.
// Supports: logo, nav links, CTA button, auth state (login/logout),
// and an optional site-wide banner that appears BELOW the main nav row,
// centered, inside the same sticky header.
//
// banner shape:
// {
//   message: string,
//   detail?: string,
//   variant?: "info" | "warning" | "success" | "error", // defaults to "warning"
//   dismissible?: boolean,
//   action?: { label: string, path?: string, onClick?: () => void }
// }

import { useState } from "react"
import { Link, NavLink } from "react-router-dom"
import { WarningAmberRounded, InfoOutlined, CheckCircleOutline, ErrorOutline, Close } from "@mui/icons-material"
import useNavAuth from "Layout/useNavAuth"

const BANNER_ICONS = {
	warning: WarningAmberRounded,
	info: InfoOutlined,
	success: CheckCircleOutline,
	error: ErrorOutline,
}

const Navbar = ({ brand, nav, cta, authLinks, banner }) => {
	const { user, logout } = useNavAuth()
	const [mobileOpen, setMobileOpen] = useState(false)
	const [bannerDismissed, setBannerDismissed] = useState(false)

	const variant = banner?.variant ?? "warning"
	const BannerIcon = BANNER_ICONS[variant] ?? BANNER_ICONS.warning
	const showBanner = Boolean(banner) && !bannerDismissed

	return (
		<header className="sticky top-0 z-50 w-full">
			{/* Status banner */}
			{banner && (
				<div role="status" aria-live="polite" className="w-full bg-bg-subtle border-b border-border overflow-hidden transition-[max-height,opacity] duration-quick" style={{ maxHeight: showBanner ? "80px" : "0px", opacity: showBanner ? 1 : 0 }}>
					<div className="relative max-w-6xl mx-auto px-12 py-2">
						<div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
							<BannerIcon style={{ fontSize: 14 }} className="flex-shrink-0 text-brand-primary" />
							<span className="text-xs font-medium text-text-primary">{banner.message ?? "This site is under construction."}</span>
							{banner.detail && <span className="text-xs text-text-secondary">{banner.detail}</span>}
							{banner.action && (
								<>
									{banner.action.path ? (
										<Link to={banner.action.path} className="text-xs font-medium text-brand-primary hover:text-brand-hover transition-colors duration-quick whitespace-nowrap">
											{banner.action.label}
										</Link>
									) : (
										<button type="button" onClick={banner.action.onClick} className="text-xs font-medium text-brand-primary hover:text-brand-hover transition-colors duration-quick whitespace-nowrap">
											{banner.action.label}
										</button>
									)}
								</>
							)}
						</div>

						{banner.dismissible && (
							<button type="button" onClick={() => setBannerDismissed(true)} aria-label="Dismiss notice" className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-component text-text-secondary hover:text-text-primary hover:bg-bg-page transition-colors duration-quick">
								<Close style={{ fontSize: 12 }} />
							</button>
						)}
					</div>
				</div>
			)}
			{/* Main nav row */}
			<div className="w-full bg-bg-surface border-b border-border">
				<div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
					{/* Logo / brand name */}
					<Link to="/" className="flex items-center gap-2.5 flex-shrink-0" onClick={() => setMobileOpen(false)}>
						{brand?.logo && <img src={brand.logo} alt={brand.name} className="h-7 w-auto" />}
						{brand?.name && <span className="font-semibold text-text-primary tracking-tight">{brand.name}</span>}
					</Link>

					{/* Desktop nav links */}
					<nav className={`hidden md:flex items-center gap-1 flex-1 ${cta ? "justify-center" : "justify-end"}`}>
						{nav?.map((item) => (
							<NavLink key={item.path} to={item.path} className={({ isActive }) => ["px-3 py-1.5 rounded-component text-sm transition-colors duration-quick", isActive ? "text-text-primary bg-bg-subtle" : "text-text-secondary hover:text-text-primary hover:bg-bg-subtle"].join(" ")}>
								{item.label}
							</NavLink>
						))}
					</nav>

					{/* Desktop right side: auth + CTA */}
					<div className="hidden md:flex items-center gap-3 flex-shrink-0">
						{user ? (
							<>
								{authLinks?.dashboard && (
									<Link to={authLinks.dashboard} className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-quick">
										Dashboard
									</Link>
								)}
								<button type="button" onClick={logout} className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-quick">
									Log out
								</button>
							</>
						) : (
							<>
								{authLinks?.login && (
									<Link to={authLinks.login} className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-quick">
										Log in
									</Link>
								)}
								{cta && (
									<Link to={cta.path} className="px-4 py-1.5 rounded-component text-sm font-medium bg-brand-primary text-brand-text hover:bg-brand-hover transition-colors duration-quick">
										{cta.label}
									</Link>
								)}
							</>
						)}
					</div>

					{/* Mobile menu toggle */}
					<button type="button" onClick={() => setMobileOpen((o) => !o)} className="md:hidden p-2 rounded-component text-text-secondary hover:text-text-primary hover:bg-bg-subtle transition-colors duration-quick" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
						{mobileOpen ? (
							<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
								<path d="M3 3l12 12M15 3L3 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						) : (
							<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
								<path d="M2 5h14M2 9h14M2 13h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
						)}
					</button>
				</div>

				{/* Mobile menu */}
				{mobileOpen && (
					<div className="md:hidden border-t border-border bg-bg-surface px-6 py-4 flex flex-col gap-1">
						{nav?.map((item) => (
							<NavLink key={item.path} to={item.path} onClick={() => setMobileOpen(false)} className={({ isActive }) => ["px-3 py-2 rounded-component text-sm transition-colors duration-quick", isActive ? "text-text-primary bg-bg-subtle" : "text-text-secondary hover:text-text-primary hover:bg-bg-subtle"].join(" ")}>
								{item.label}
							</NavLink>
						))}

						<div className="border-t border-border mt-2 pt-3 flex flex-col gap-2">
							{user ? (
								<>
									{authLinks?.dashboard && (
										<Link to={authLinks.dashboard} onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors duration-quick">
											Dashboard
										</Link>
									)}
									<button
										type="button"
										onClick={() => {
											logout()
											setMobileOpen(false)
										}}
										className="px-3 py-2 text-sm text-left text-text-secondary hover:text-text-primary transition-colors duration-quick"
									>
										Log out
									</button>
								</>
							) : (
								<>
									{authLinks?.login && (
										<Link to={authLinks.login} onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors duration-quick">
											Log in
										</Link>
									)}
									{cta && (
										<Link to={cta.path} onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-component text-sm font-medium text-center bg-brand-primary text-brand-text hover:bg-brand-hover transition-colors duration-quick">
											{cta.label}
										</Link>
									)}
								</>
							)}
						</div>
					</div>
				)}
			</div>
		</header>
	)
}

export default Navbar
